import { NavLink } from 'react-router-dom';
import { IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand } from '@tabler/icons-react';
import { NAVIGATION } from '@/routes/navigation';
import { useUiStore } from '@/stores/uiStore';
import { usePermission } from '@/hooks/usePermission';
import { cn } from '@/utils/cn';

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);

  return (
    <aside
      className={cn(
        'sticky top-0 flex h-screen shrink-0 flex-col border-r border-border-subtle bg-bg-elevated transition-all duration-base',
        collapsed ? 'w-[76px]' : 'w-64',
      )}
    >
      <div className="flex h-[72px] shrink-0 items-center gap-3 border-b border-border-subtle px-5">
        <div className="relative flex h-8 w-8 shrink-0 items-center justify-center">
          <div className="absolute inset-0 rounded-full bg-brand-gradient opacity-40 blur-md" />
          <img src="/vynzo-logo.svg" alt="Vynzo" className="relative h-8 w-8 shrink-0" />
        </div>
        {!collapsed && <span className="font-display text-lg font-extrabold tracking-tight text-text-primary">Vynzo</span>}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {NAVIGATION.map((group) => (
          <NavGroupSection key={group.label} group={group} collapsed={collapsed} />
        ))}
      </nav>

      <button
        onClick={toggleSidebar}
        className="focus-ring flex h-12 shrink-0 items-center justify-center gap-2 border-t border-border-subtle text-text-tertiary hover:bg-bg-surface-hover hover:text-text-primary"
      >
        {collapsed ? <IconLayoutSidebarLeftExpand className="h-4 w-4" /> : <IconLayoutSidebarLeftCollapse className="h-4 w-4" />}
        {!collapsed && <span className="text-xs">Collapse</span>}
      </button>
    </aside>
  );
}

function NavGroupSection({ group, collapsed }: { group: (typeof NAVIGATION)[number]; collapsed: boolean }) {
  const hasAnyPermission = usePermission(group.items.filter((i) => i.permission).map((i) => i.permission!));
  const visibleItems = group.items.filter((item) => !item.permission);
  const hasVisibleItems = visibleItems.length > 0 || hasAnyPermission;

  if (!hasVisibleItems) return null;

  return (
    <div className="mb-5">
      {!collapsed && <p className="mb-2 px-3 text-[11px] font-bold uppercase tracking-wider text-text-tertiary">{group.label}</p>}
      <div className="flex flex-col gap-0.5">
        {group.items.map((item) => (
          <SidebarLink key={item.path} item={item} collapsed={collapsed} />
        ))}
      </div>
    </div>
  );
}

function SidebarLink({ item, collapsed }: { item: (typeof NAVIGATION)[number]['items'][number]; collapsed: boolean }) {
  const allowed = usePermission(item.permission ?? []);
  if (item.permission && !allowed) return null;

  const Icon = item.icon;

  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) =>
        cn(
          'focus-ring relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-base',
          isActive
            ? 'bg-gradient-to-r from-brand-primary-subtle to-transparent text-brand-primary'
            : 'text-text-secondary hover:bg-bg-surface-hover hover:text-text-primary',
        )
      }
      title={collapsed ? item.label : undefined}
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-brand-gradient shadow-glow-purple" />}
          <Icon className="h-[18px] w-[18px] shrink-0" />
          {!collapsed && <span>{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}
